'use client';

import { useState, useEffect } from 'react';
import { 
  Store, Package, ShoppingBag, ArrowLeft, User, 
  ShoppingBasket, Printer, CheckCircle2, Layers, Link2, Check, Search
} from 'lucide-react';
import styles from './VendasModule.module.css';

export function VendasModule({ reverse = false }: { reverse?: boolean }) {
  // Fases da Animação:
  // 0: idle
  // 1: typing product
  // 2: item added to cart
  // 3: checkout button clicked
  // 4: pdv success overlay
  // 5: hub sync panel opens (syncing state)
  // 6: hub sync complete (stock drops)
  const [phase, setPhase] = useState(0);
  const [typedText, setTypedText] = useState("");

  const productName = "AirPods Pro 2ª Geração";

  useEffect(() => {
    let timeout: NodeJS.Timeout;

    if (phase === 0) {
      setTypedText('');
      timeout = setTimeout(() => setPhase(1), 1000);
    } 
    else if (phase === 1) {
      let i = 0;
      const typeInterval = setInterval(() => {
        setTypedText(productName.substring(0, i + 1));
        i++;
        if (i === productName.length) {
          clearInterval(typeInterval);
          setTimeout(() => setPhase(2), 600);
        }
      }, 50);
      return () => clearInterval(typeInterval);
    }
    else if (phase === 2) timeout = setTimeout(() => setPhase(3), 1500); // Wait in cart
    else if (phase === 3) timeout = setTimeout(() => setPhase(4), 600);  // Click animation
    else if (phase === 4) timeout = setTimeout(() => setPhase(5), 1500); // Show Success Screen
    else if (phase === 5) timeout = setTimeout(() => setPhase(6), 2500); // Show Hub Syncing
    else if (phase === 6) timeout = setTimeout(() => setPhase(0), 4000); // Show Synced & Reset

    return () => clearTimeout(timeout);
  }, [phase]);

  // Constantes de Interface
  const isItemInCart = phase >= 2 && phase <= 4;
  const isClicked = phase === 3 || phase === 4;
  const showSuccess = phase === 4;
  const showHub = phase >= 5;
  const isSynced = phase === 6;

  const currentTotal = isItemInCart ? "R$ 92,00" : "R$ 0,00";

  return (
    <div className={styles.moduleWrapper} style={{ flexDirection: reverse ? 'row-reverse' : 'row' }}>
      
      {/* ==============================================
          COLUNA ESQUERDA: TEXTO E ARGUMENTOS
          ============================================== */}
      <div className={styles.textContent}>
        <span className={styles.badge}>Vendas, PDV & Marketplaces</span>
        <h3 className={styles.title}>
          Seu Hub Central de Pedidos e Estoque
        </h3>
        <p className={styles.description}>
          Venda no balcão, lance a nota e veja a mágica acontecer: o seu estoque é baixado instantaneamente no Mercado Livre, Shopee e na sua Loja Virtual. Sem furos, sem dor de cabeça.
        </p>

        <div className={styles.featureList}>
          <div className={styles.featureItem}>
            <div className={styles.featureIcon}><Printer size={16} /></div>
            PDV Frente de Caixa ultra rápido com emissão de NFC-e.
          </div>
          <div className={styles.featureItem}>
            <div className={styles.featureIcon}><Layers size={16} /></div>
            Hub nativo de Marketplaces (ML, Shopee, TikTok Shop).
          </div>
          <div className={styles.featureItem}>
            <div className={styles.featureIcon}><Store size={16} /></div>
            Catálogo Online (Loja Virtual) integrado sem custo extra.
          </div>
        </div>
      </div>

      {/* ==============================================
          COLUNA DIREITA: MOCKUP DO PDV & HUB
          ============================================== */}
      <div className={styles.mockupContainer}>
        <div className={styles.appWindow}>
          
          {/* HEADER DO PDV */}
          <div className={styles.appHeader}>
            <div className={styles.backBtn}><ArrowLeft size={16} /></div>
            <div>
              <div className={styles.headerTitle}>Nova Proposta de Venda</div>
              <div className={styles.headerSub}>Frente de Caixa (PDV) de alta performance.</div>
            </div>
          </div>

          <div className={styles.appBody}>
            
            {/* LADO ESQUERDO DO PDV */}
            <div className={styles.posLeft}>
              
              <div className={styles.posCard}>
                <div className={styles.cardTop}>
                  <div className={styles.cardIcon}><User size={14}/></div> Identificação do Cliente
                </div>
                <div className={styles.searchBar}>
                  <Search size={14} style={{marginRight: '8px'}} /> Digite para buscar na base...
                </div>
              </div>

              <div className={styles.posCard} style={{flex: 1}}>
                <div className={styles.cardTop}>
                  <div className={`${styles.cardIcon} ${styles.orange}`}><ShoppingBasket size={14}/></div> Carrinho de Compras
                </div>
                <div className={styles.barcodeInput}>
                  <span style={{fontFamily: 'monospace', fontWeight: 800, marginRight: '8px', color: '#cbd5e1'}}>||||</span> 
                  {typedText || <span style={{color: '#94a3b8'}}>Digite o nome ou passe o leitor...</span>}
                  {phase === 1 && <span className={styles.cursor}></span>}
                </div>
                
                <div className={styles.cartHeader}>
                  <div className={styles.col1}>Item Comercializado</div>
                  <div className={styles.col2}>Qtd.</div>
                  <div className={styles.col3}>Subtotal</div>
                </div>

                {isItemInCart ? (
                  <div className={styles.cartItem}>
                    <div className={styles.itemPhoto}><Package size={16} color="#94a3b8"/></div>
                    <div className={styles.col1}>
                      <div className={styles.itemName}>Fone AirPods Pro 2ª Geração</div>
                      <div className={styles.itemSku}>SKU: AirPodsPRO</div>
                    </div>
                    <div className={styles.col2} style={{fontWeight: 800, color: '#1e293b'}}>1</div>
                    <div className={styles.col3} style={{fontWeight: 800, color: '#ea580c'}}>R$ 92,00</div>
                  </div>
                ) : (
                  <div className={styles.cartEmpty}>
                    <ShoppingBasket size={32} style={{marginBottom: '8px'}} />
                    Carrinho Vazio
                  </div>
                )}
              </div>

            </div>

            {/* LADO DIREITO (CHECKOUT) */}
            <div className={styles.posRight}>
              <div className={styles.checkoutHeader}>
                <Printer size={16} /> Finalização
              </div>
              <div className={styles.checkoutBody}>
                
                <div>
                  <div className={styles.checkoutLabel}>Canal de Venda</div>
                  <div className={styles.fakeInput} style={{display: 'flex', alignItems: 'center', padding: '0 10px', fontSize: '0.75rem', fontWeight: 600}}>
                    Balcão (Loja Física)
                  </div>
                </div>

                <div>
                  <div className={styles.checkoutLabel}>Método de Pagamento</div>
                  <div className={styles.fakeInput} style={{display: 'flex', alignItems: 'center', padding: '0 10px', fontSize: '0.75rem', fontWeight: 600}}>
                    PIX (Aprovado)
                  </div>
                </div>

                <div className={styles.totalsArea}>
                  <div className={styles.totalRow}><span>Subtotal dos Itens</span> <span>{currentTotal}</span></div>
                  <div className={styles.totalRow}><span>Desconto Manual</span> <span>- R$ 0,00</span></div>
                  <div className={styles.totalFinal}>
                    <span style={{fontSize: '0.7rem', color: '#1e293b'}}>TOTAL DO PEDIDO</span> 
                    <span>{currentTotal}</span>
                  </div>
                </div>

                <div className={`${styles.finishBtn} ${isClicked ? styles.clicked : ''}`}>
                  <CheckCircle2 size={18} /> Concluir Venda
                </div>

              </div>
            </div>

          </div>

          {/* OVERLAY SUCESSO (PDV) */}
          <div className={`${styles.successOverlay} ${showSuccess ? styles.active : ''}`}>
            <CheckCircle2 size={56} style={{marginBottom: '16px'}} />
            <h2 style={{fontSize: '1.8rem', fontWeight: 800, margin: 0}}>Venda Concluída!</h2>
            <p style={{fontWeight: 600, opacity: 0.9}}>Baixando estoque em todos os canais...</p>
          </div>

          {/* OVERLAY HUB DE MARKETPLACES (A MÁGICA DA SINCRONIZAÇÃO) */}
          <div className={`${styles.hubOverlay} ${showHub ? styles.active : ''}`}>
            <div className={styles.hubWindow}>
              
              <div className={styles.hubHeader}>
                <div className={styles.hubIcon}><Layers size={24} /></div>
                <div>
                  <h3 className={styles.hubTitle}>Hub de Marketplaces</h3>
                  <span className={styles.hubSub}>Gerencie anúncios, estoques e pedidos do Mercado Livre, Shopee...</span>
                </div>
              </div>

              <div className={styles.hubList}>
                {/* Linha Mercado Livre */}
                <div className={styles.hubItem}>
                  <div>
                    <span className={styles.mlBadge}>MERCADO LIVRE</span>
                    <div className={styles.prodName}>Fone AirPods Pro 2ª Geração</div>
                    <div className={styles.prodSku}>ID: MLB3670942259</div>
                  </div>
                  <div className={styles.stockArea}>
                    <div className={styles.stockPrice}>R$ 92.00</div>
                    <div className={styles.stockNum}>
                      📦 Disp: 
                      {isSynced ? (
                        <span className={styles.stockChange}>529</span>
                      ) : (
                        <span>530</span>
                      )}
                    </div>
                  </div>
                  <div>
                    {isSynced ? (
                      <div className={`${styles.syncTag} ${styles.synced}`}><Check size={14}/> Sincronizado</div>
                    ) : (
                      <div className={`${styles.syncTag} ${styles.syncing}`}><div className={styles.spinner}></div> Sincronizando</div>
                    )}
                  </div>
                </div>

                {/* Linha Shopee */}
                <div className={styles.hubItem}>
                  <div>
                    <span className={styles.shopeeBadge}>SHOPEE BRASIL</span>
                    <div className={styles.prodName}>Fone AirPods Pro 2ª Geração</div>
                    <div className={styles.prodSku}>ID: SHP8849201</div>
                  </div>
                  <div className={styles.stockArea}>
                    <div className={styles.stockPrice}>R$ 92.00</div>
                    <div className={styles.stockNum}>
                      📦 Disp: 
                      {isSynced ? (
                        <span className={styles.stockChange}>529</span>
                      ) : (
                        <span>530</span>
                      )}
                    </div>
                  </div>
                  <div>
                    {isSynced ? (
                      <div className={`${styles.syncTag} ${styles.synced}`}><Check size={14}/> Sincronizado</div>
                    ) : (
                      <div className={`${styles.syncTag} ${styles.syncing}`}><div className={styles.spinner}></div> Sincronizando</div>
                    )}
                  </div>
                </div>

              </div>
            </div>
          </div>

        </div>
      </div>

    </div>
  );
}